import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { isAxiosError } from 'axios';

import {
  getCandidateV2Options,
  getIdentityOptions,
} from 'client/@tanstack/react-query.gen';
import { NotFound } from 'pages/NotFound';

/**
 * The one page for a human: `/people/:identityId`.
 *
 * Loads the identity, and its candidate profile when it has one. The
 * candidate tabs live under the pathless `_candidate` layout, which sends an
 * identity without a candidate to its Accounts tab.
 */
export const Route = createFileRoute(
  '/_private/_route/_internal/people/$identityId',
)({
  // A notFound() thrown in beforeLoad always goes to the root route, which
  // drops the app shell. So beforeLoad only records that there is no
  // identity, and the loader throws, which this notFoundComponent catches.
  notFoundComponent: NotFound,
  beforeLoad: async ({ context, location, params, preload }) => {
    const { queryClient } = context;
    // Always fresh: a merge can leave the cached identity out of date, and
    // the redirect below must see the merge.
    const identity = await queryClient
      .fetchQuery({
        ...getIdentityOptions({ path: { identity_uuid: params.identityId } }),
        staleTime: 0,
      })
      .catch((error: unknown) => {
        // the api answers 404 for an unknown identity and 422 for an id that
        // is not a uuid
        const status = isAxiosError(error) ? error.response?.status : undefined;
        if (status === 404 || status === 422) {
          return null;
        }
        throw error;
      });

    // With no candidate either, the child routes send the link to the
    // Accounts tab, and the loader below shows the not found page there.
    if (!identity) {
      return { candidate: null, identity: null };
    }

    // This identity was merged into another one and holds nothing now. Old
    // links are still out there, so send them to the survivor, keeping the
    // tab and query string. Merging re-points earlier signposts at each new
    // survivor, so one hop is enough.
    if (identity.merged_into_uuid) {
      // A hover must not redirect: this router leaves the match it
      // redirected from stuck, and the click that follows never loads. A
      // failed preload is not reused, so the click runs this again and
      // redirects. The router logs the error to the console.
      if (preload) {
        throw new Error(
          `Not preloading merged identity ${params.identityId}; a click redirects`,
        );
      }
      throw redirect({
        href: location.href.replace(
          `/people/${params.identityId}`,
          `/people/${identity.merged_into_uuid}`,
        ),
        replace: true,
      });
    }

    const candidate = identity.candidate
      ? await queryClient.ensureQueryData(
          getCandidateV2Options({
            path: { candidate_uuid: identity.candidate.uuid },
          }),
        )
      : null;

    return { candidate, identity };
  },
  loader: ({ context }) => {
    if (!context.identity) {
      throw notFound();
    }
  },
});
